import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAccountsApi, type CompanyStatus, type ManagerAccount } from '@/services/managers';

export interface Company {
  id: string;
  name: string;
  legalName: string;
  cnpj: string;
  logoUrl?: string;
  status?: CompanyStatus;
}

/**
 * Empresa (conta de gestor) atual e lista de empresas do usuário.
 * A lista alimenta o seletor de troca de empresa; a troca em si passa pela
 * action switchCompany, que recarrega o app para carregar os dados da nova empresa.
 */
export const useCompanyStore = defineStore('company', () => {
  const accountsApi = useAccountsApi();

  // States
  const company = ref<Company | null>(null);
  const companies = ref<Company[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const hasCompany = computed(() => !!company.value);
  const companyName = computed(() => company.value?.name || '');
  const companyCnpj = computed(() => company.value?.cnpj || '');

  // Actions
  const setCompany = (companyData: Company) => {
    company.value = companyData;
  };

  const setCompanies = (companiesData: Company[]) => {
    companies.value = companiesData;
  };

  const selectCompany = (companyId: string) => {
    const selected = companies.value.find((item) => item.id === companyId);
    if (selected) {
      company.value = selected;
    }
  };

  /** Lista as empresas do gestor (GET /manager/accounts) para o seletor. */
  const fetchCompanies = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const accounts = await accountsApi.listAccounts();
      companies.value = accounts.map(_toCompany);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao carregar empresas';
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Troca a empresa atual sem relogar: seleciona a conta na API (regrava o
   * X-ACCOUNT-ID + cookie) e recarrega o app. O reload limpa todas as stores de
   * domínio e o guard do router reroteia terms/onboarding na empresa nova.
   */
  const switchCompany = async (accountId: string) => {
    const target = companies.value.find((item) => item.id === accountId);
    isLoading.value = true;
    error.value = null;
    try {
      await accountsApi.selectAccount(accountId);
      if (target) setCompany(target);
      window.location.assign('/');
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao trocar de empresa';
      isLoading.value = false;
    }
  };

  // Private helpers
  const _toCompany = (account: ManagerAccount): Company => ({
    id: account.id,
    name: account.name,
    legalName: account.name,
    cnpj: account.cnpj ?? '',
    status: account.status,
  });

  return {
    company,
    companies,
    isLoading,
    error,
    hasCompany,
    companyName,
    companyCnpj,
    setCompany,
    setCompanies,
    selectCompany,
    fetchCompanies,
    switchCompany,
  };
}, {
  // Persiste a empresa selecionada para sobreviver a reloads (igual ao businessCnpj da sessão).
  persist: { paths: ['company'] },
});
